import {useCallback, useEffect, useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {useTouchDevice} from '@pexip/components';

import {isMobileDevice} from '../utils/isMobileDevice';
import {desktopToolbarWidth, interfacesMargin} from '../constants';

const coreHeaderHeight = 55;
const headerHeight = coreHeaderHeight + interfacesMargin;

const footerHeight = 82;
const mobileFooterHeight = interfacesMargin + footerHeight + interfacesMargin;
const desktopFooterHeight = interfacesMargin;

export interface ControlsOverlap {
    desktopToolbar: boolean;
    header: boolean;
    footer: boolean;
}

export const isControlsOverlapTruthy = (controlsOverlap: ControlsOverlap) =>
    controlsOverlap.desktopToolbar ||
    controlsOverlap.header ||
    controlsOverlap.footer;

export const useControlsRelativePosition = (
    ref: React.RefObject<HTMLDivElement>,
) => {
    const [controlsOverlap, setControlsOverlap] = useState<ControlsOverlap>({
        desktopToolbar: false,
        header: false,
        footer: false,
    });
    const isTouchDevice = useTouchDevice();
    const footerHeight = useMemo(
        () => (isTouchDevice ? mobileFooterHeight : desktopFooterHeight),
        [isTouchDevice],
    );

    const {i18n} = useTranslation();
    const dir = i18n.dir(i18n.language);

    const update = useCallback(
        (force?: ControlsOverlap) => {
            if (!ref.current) {
                setControlsOverlap({
                    desktopToolbar: false,
                    header: false,
                    footer: false,
                });
                return;
            }
            if (force) {
                setControlsOverlap(force);
                return;
            }
            const el = ref.current;
            const rect = el.getBoundingClientRect();

            let overlapsDesktopToolbar =
                !isMobileDevice() &&
                el.offsetLeft <= desktopToolbarWidth + interfacesMargin;

            if (dir === 'rtl') {
                overlapsDesktopToolbar =
                    !isMobileDevice() &&
                    rect.right >=
                        window.innerWidth -
                            desktopToolbarWidth -
                            interfacesMargin;
            }

            const overlapsHeader = el.offsetTop <= headerHeight;
            const overlapsFooter =
                rect.bottom >= window.innerHeight - footerHeight;

            setControlsOverlap({
                desktopToolbar: overlapsDesktopToolbar,
                header: overlapsHeader,
                footer: overlapsFooter,
            });
        },
        [footerHeight, ref, dir],
    );

    useEffect(() => {
        update();
    }, [update]);

    return {controlsOverlap, update};
};
